import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  convertMinsToHrsMins,
  formatDollarAmount,
} from '../../utils/global/helper';

export interface ServiceCheckoutFormItem {
  type: 'service';
  id: string;
  name: string;
  provider?: string;
  duration?: number; // minutes
  commissionRate?: string; // formatted percent, e.g. "60%"
  price: number;
  onRemove?: () => void;
  disabled?: boolean;
  onClick?: () => void;
}

export interface OrderCheckoutFormItem {
  type: 'item';
  id: string;
  name: string;
  price: number;
  quantity: number;
  onRemove?: () => void;
  onClick?: () => void;
}

export type CheckoutFormItem = ServiceCheckoutFormItem | OrderCheckoutFormItem;

interface Props {
  title?: string;
  items: CheckoutFormItem[];
}

interface CheckoutRowProps {
  name: string;
  price: number;
  // service-specific
  provider?: string;
  duration?: number;
  commissionRate?: string;
  disabled?: boolean;
  // item-specific
  quantity?: number;
  // shared
  onRemove?: () => void;
  onClick?: () => void;
}

function CheckoutRow({
  name,
  price,
  provider,
  duration,
  commissionRate,
  disabled,
  quantity,
  onRemove,
  onClick,
}: CheckoutRowProps) {
  const theme = useTheme();
  const isClickable = !!onClick && !disabled;
  const displayPrice =
    quantity != null
      ? `${quantity > 1 ? `(${quantity}) ` : ''}${formatDollarAmount(
          price * quantity,
        )}`
      : formatDollarAmount(price);
  return (
    <Box
      display="flex"
      alignItems="center"
      width="100%"
      gap={1}
      p={1.5}
      boxSizing="border-box"
      borderRadius="8px"
      onClick={isClickable ? onClick : undefined}
      sx={{
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'background 0.1s ease',
        '&:hover': isClickable ? { bgcolor: 'action.hover' } : {},
      }}>
      <Stack flexGrow={1} minWidth={0} gap={0.5}>
        <Typography variant="subtitle2" noWrap>
          {name}
        </Typography>
        {provider && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            noWrap>
            {provider}
          </Typography>
        )}
        {!!duration && (
          <Typography variant="caption" color="text.secondary" display="block">
            {convertMinsToHrsMins(duration, true)}
          </Typography>
        )}
        {!!commissionRate && (
          <Typography variant="caption" color="text.secondary" display="block">
            Commission Rate: {commissionRate}
          </Typography>
        )}
      </Stack>
      <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
        <Typography
          variant="body2"
          color={quantity != null ? 'text.secondary' : undefined}>
          {displayPrice}
        </Typography>
        <IconButton
          size="small"
          onClick={
            onRemove
              ? e => {
                  e.stopPropagation();
                  onRemove();
                }
              : undefined
          }
          sx={{
            p: 1,
            visibility: onRemove ? 'visible' : 'hidden',
            color: theme.palette.outlineGrey?.main ?? 'text.disabled',
          }}>
          <CloseRoundedIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Box>
    </Box>
  );
}

function CheckoutFormItems({ title = 'Transaction Details', items }: Props) {
  const theme = useTheme();
  const services = items.filter(
    (i): i is ServiceCheckoutFormItem => i.type === 'service',
  );
  const orderItems = items.filter(
    (i): i is OrderCheckoutFormItem => i.type === 'item',
  );

  const hasItems = !!services.length || !!orderItems.length;

  return (
    <Box className="input-group" width="100%">
      <Stack gap={0.5}>
        <Typography variant="h2">{title}</Typography>
        {hasItems && (
          <Typography variant="body2">Click on an item to edit</Typography>
        )}
      </Stack>
      {hasItems && (
        <Stack width="100%" gap={0.5}>
          {services.map(item => (
            <CheckoutRow
              key={item.id}
              name={item.name}
              price={item.price}
              provider={item.provider}
              duration={item.duration}
              commissionRate={item.commissionRate}
              disabled={item.disabled}
              onRemove={item.onRemove}
              onClick={item.onClick}
            />
          ))}
          {services.length > 0 && orderItems.length > 0 && (
            <Box
              borderTop={`1px solid ${theme.palette.divider}`}
              mt={1.5}
              mb={1.5}
            />
          )}
          {orderItems.map(item => (
            <CheckoutRow
              key={item.id}
              name={item.name}
              price={item.price}
              quantity={item.quantity}
              onRemove={item.onRemove}
              onClick={item.onClick}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default CheckoutFormItems;
